import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Check, Globe2, Search, Users, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../global-components/src';
import { PACOCA_API_URL } from '../../../global-components/src/utils/vars';

const MAX_COMMUNITIES = 5;
const PAGE_SIZE = 11;

type Community = {
  id: number;
  name: string;
  description?: string;
};

interface Props {
  selectedCommunityIds: number[];
  onChange: (communityIds: number[]) => void;
}

export default function PostDestinationSelector({
  selectedCommunityIds,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [communities, setCommunities] = useState<Community[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  const loadCommunities = useCallback(
    async (nextPage: number, term: string, replace = false) => {
      if (!user?.id || !token) return;

      const activeRequest = ++requestId.current;
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${PACOCA_API_URL}/communities/by-user/${user.id}`,
          {
            params: { page: nextPage, ...(term ? { search: term } : {}) },
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (activeRequest !== requestId.current) return;

        const nextCommunities: Community[] = data?.communities ?? [];
        setCommunities(current => {
          const merged = replace ? nextCommunities : [...current, ...nextCommunities];
          return Array.from(
            new Map(merged.map(community => [community.id, community])).values(),
          );
        });
        setPage(nextPage);
        setHasMore(nextCommunities.length === PAGE_SIZE);
        setError('');
      } catch {
        if (activeRequest === requestId.current) {
          setError(
            t('post.destination.loadError'),
          );
        }
      } finally {
        if (activeRequest === requestId.current) setLoading(false);
      }
    },
    [t, token, user?.id],
  );

  useEffect(() => {
    if (!open) return;

    setSearch('');
    setCommunities([]);
    setPage(1);
    setHasMore(true);
    setError('');
    loadCommunities(1, '', true).catch(() => {});

    return () => {
      requestId.current += 1;
    };
  }, [loadCommunities, open]);

  useEffect(() => {
    if (!open || !search) return;

    const timer = window.setTimeout(() => {
      loadCommunities(1, search.trim(), true).catch(() => {});
    }, 300);

    return () => window.clearTimeout(timer);
  }, [loadCommunities, open, search]);

  const toggleCommunity = (communityId: number) => {
    if (selectedCommunityIds.includes(communityId)) {
      onChange(selectedCommunityIds.filter(id => id !== communityId));
      setError('');
      return;
    }

    if (selectedCommunityIds.length >= MAX_COMMUNITIES) {
      setError(
        t('post.destination.limit'),
      );
      return;
    }

    onChange([...selectedCommunityIds, communityId]);
    setError('');
  };

  const publicLabel = t('post.destination.public');
  const selectedLabel = selectedCommunityIds.length
    ? t('post.destination.communities', {
        count: selectedCommunityIds.length,
        })
    : publicLabel;

  return (
    <>
      <button
        type="button"
        className="ap-destination-trigger"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <Globe2 size={16} />
        <span>{selectedLabel}</span>
      </button>

      {open && (
        <div
          className="ap-destination-overlay"
          role="presentation"
          onMouseDown={() => setOpen(false)}
        >
          <section
            className="ap-destination-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={t('post.destination.title')}
            onMouseDown={event => event.stopPropagation()}
          >
            <header className="ap-destination-header">
              <div>
                <h4>{t('post.destination.title')}</h4>
                <p>{t('post.destination.subtitle')}</p>
              </div>
              <button
                type="button"
                className="ap-destination-close"
                onClick={() => setOpen(false)}
                aria-label={t('post.destination.close')}
              >
                <X size={20} />
              </button>
            </header>

            <button
              type="button"
              className="ap-destination-public"
              onClick={() => {
                onChange([]);
                setError('');
              }}
              aria-pressed={selectedCommunityIds.length === 0}
            >
              <span className="ap-destination-public-icon">
                <Globe2 size={20} />
              </span>
              <span className="ap-destination-copy">
                <strong>{publicLabel}</strong>
                <small>
                  {t('post.destination.publicDescription')}
                </small>
              </span>
              {selectedCommunityIds.length === 0 && (
                <span className="ap-destination-check"><Check size={15} /></span>
              )}
            </button>

            <label className="ap-destination-search">
              <Search size={17} />
              <input
                value={search}
                onChange={event => {
                  const nextSearch = event.target.value;
                  setSearch(nextSearch);
                  if (!nextSearch) loadCommunities(1, '', true).catch(() => {});
                }}
                placeholder={t('post.destination.search')}
                autoComplete="off"
              />
              {!!search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    loadCommunities(1, '', true).catch(() => {});
                  }}
                  aria-label={t('post.destination.clearSearch')}
                >
                  <X size={16} />
                </button>
              )}
            </label>

            {!!error && <p className="ap-destination-error">{error}</p>}

            <div className="ap-destination-list">
              {communities.map(community => {
                const selected = selectedCommunityIds.includes(community.id);
                const disabled = !selected && selectedCommunityIds.length >= MAX_COMMUNITIES;
                return (
                  <button
                    key={community.id}
                    type="button"
                    className={`ap-destination-community${disabled ? ' is-disabled' : ''}`}
                    onClick={() => toggleCommunity(community.id)}
                    aria-pressed={selected}
                    disabled={disabled}
                  >
                    <span className="ap-destination-avatar">
                      {community.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className="ap-destination-copy">
                      <strong>{community.name}</strong>
                      {!!community.description && <small>{community.description}</small>}
                    </span>
                    {selected && <span className="ap-destination-check"><Check size={15} /></span>}
                  </button>
                );
              })}

              {!loading && communities.length === 0 && (
                <div className="ap-destination-empty">
                  <Users size={28} />
                  <span>
                    {search
                      ? t('post.destination.noResults')
                      : t('post.destination.empty')}
                  </span>
                </div>
              )}

              {loading && <div className="ap-destination-loading">...</div>}
              {!loading && hasMore && communities.length > 0 && (
                <button
                  type="button"
                  className="ap-destination-more"
                  onClick={() => loadCommunities(page + 1, search.trim()).catch(() => {})}
                >
                  {t('generics.loadMore')}
                </button>
              )}
            </div>

            <footer className="ap-destination-footer">
              <span>
                {selectedCommunityIds.length === 0
                  ? publicLabel
                  : `${selectedCommunityIds.length}/${MAX_COMMUNITIES}`}
              </span>
              <button type="button" onClick={() => setOpen(false)}>
                {t('post.destination.done')}
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}
